import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  IFilter,
  IQueryParam,
  IPopulate,
  IConfigPerfil,
  TipoEntidadConfigPerfil,
  AccionesRol,
} from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';

import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { ConfigPerfilService } from '../../../../auxiliares/servicios/http/config-perfil.service';
import { CrearEditarConfigPerfilComponent } from '../crear-editar-config-perfil/crear-editar-config-perfil.component';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';
import { escaparHtml } from '../../../../auxiliares/html-seguro.util';
import { estadoHtml } from '../../../../auxiliares/tabla/celdas';

@Component({
  imports: [AuxiliaresModule],
  selector: 'app-listado-config-perfil',
  templateUrl: './listado-config-perfil.component.html',
  styleUrl: './listado-config-perfil.component.scss',
})
export class ListadoConfigPerfilComponent implements OnInit, OnDestroy {
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private service = inject(ConfigPerfilService);
  private formularios = inject(FormularioDialogService);
  private route = inject(ActivatedRoute);
  private navigator = inject(Router);

  public tipoEntidad: TipoEntidadConfigPerfil; //Entidad a la que pertenecen los perfiles
  //// Tabla nueva
  public name = 'ListadoConfigPerfilComponent';
  public configsPerfil?: IConfigPerfil[];
  public totalCount = 0;
  public columnas?: IColumnas<IConfigPerfil>[];
  //Subs
  public configsPerfil$?: Subscription;
  //
  public botonCrear: ICrearBoton;
  public search: IRegExpSearch = {
    fields: ['nombre', 'tipoConfig'],
  };

  //Permisos
  private puedeCrear: AccionesRol;
  private puedeEditar: AccionesRol;
  private puedeEliminar: AccionesRol;

  // FILTROS
  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public filtros: IFiltroTabla[] = [];

  //Query
  public filter: IFilter<IConfigPerfil> = {};

  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    sort: '-fechaCreacion',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: false,
    childrenLevel: 0,
  };

  // Listados
  public async listar(): Promise<void> {
    const filter: IFilter<IConfigPerfil> = this.query.filter
      ? JSON.parse(this.query.filter)
      : {};
    filter.tipoEntidad = this.tipoEntidad;
    this.query.filter = JSON.stringify(filter);
    const populate: IPopulate = [
      {
        path: 'cliente',
        select: 'nombre',
      },
      {
        path: 'valores.perfilDimming',
      },
    ];
    this.query.populate = JSON.stringify(populate);

    //
    this.configsPerfil$?.unsubscribe();
    this.configsPerfil$ = this.lista2
      .configPerfils(this.query, 30)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.configsPerfil = data.datos;
      });
  }

  // Acciones
  public async crear() {
    const dialogRef = this.formularios.abrir(CrearEditarConfigPerfilComponent, {
      tamano: 'mediano',
      data: {
        tipoEntidad: this.tipoEntidad,
      },
    });
    const res = await firstValueFrom(dialogRef.afterClosed());
    if (res) {
      this.listar();
    }
  }

  public async editar(configPerfil: IConfigPerfil) {
    const dialogRef = this.formularios.abrir(CrearEditarConfigPerfilComponent, {
      tamano: 'mediano',
      data: {
        edit: configPerfil,
        tipoEntidad: this.tipoEntidad,
      },
    });
    const res = await firstValueFrom(dialogRef.afterClosed());
    if (res) {
      this.listar();
    }
  }

  public async eliminar(configPerfil: IConfigPerfil) {
    const esDimming =
      configPerfil.tipoConfig === 'Luminaria ACTIS FING Dimming';
    const mensaje = esDimming
      ? `¿Querés eliminar el perfil <strong>${escaparHtml(configPerfil.nombre)}</strong>?<br><br><strong>Atención:</strong> este perfil de dimerizado se desvinculará de todos los perfiles generales que lo tengan referenciado.`
      : `¿Querés eliminar el perfil <strong>${escaparHtml(configPerfil.nombre)}</strong>?`;
    const res = await this.dialogService.confirm('Confirmá la acción', mensaje);
    if (!res) return;
    try {
      await this.service.delete(configPerfil._id!);
      this.helper.notifSuccess('Eliminado correctamente');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private setColumnas() {
    const columnas: IColumnas<IConfigPerfil>[] = [
      // Nombre
      {
        header: {
          label: 'Nombre',
          sortable: true,
          sort: 'nombre',
        },
        row: {
          html: (dato) => {
            let html = `<div class="tabla-celda-col"><span class="tabla-principal">${escaparHtml(dato.nombre)}</span>`;
            if (dato.global) {
              html += `<span>${estadoHtml({ texto: 'Global', tono: 'info', icono: 'public' })}</span>`;
            }
            return html + '</div>';
          },
        },
      },
      // Tipo Config
      {
        header: {
          label: 'Tipo de configuración',
          sortable: true,
        },
        row: {
          field: 'tipoConfig',
        },
      },
    ];

    // Cliente
    if (HelperService.getTipoCliente() !== 'Final') {
      {
        columnas.push({
          header: {
            label: 'Cliente',
          },
          row: {
            html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
            tooltip: (dato) =>
              this.clienteJerarquia.tooltipJerarquia(dato.cliente),
            noData: 'Sin cliente',
          },
        });
      }
    }

    columnas.push(
      // Acciones
      {
        header: {
          label: 'Acciones',
        },
        row: {
          acciones: [
            {
              tipo: 'material',
              color: 'primary',
              icon: 'edit',
              tooltip: 'Editar',
              click: (dato) => this.editar(dato),
              disabled: (dato) =>
                !this.helper.tienePermiso(this.puedeEditar) ||
                (dato.global && !this.helper.esNivel0()),
            },
            {
              tipo: 'material',
              color: 'warn',
              icon: 'delete',
              tooltip: 'Eliminar',
              click: (dato) => this.eliminar(dato),
              disabled: (dato) =>
                !this.helper.tienePermiso(this.puedeEliminar) ||
                (dato.global && !this.helper.esNivel0()),
            },
          ],
        },
      },
    );

    this.columnas = columnas;
  }

  // Hooks

  async ngOnInit() {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.ancestro);
    }
    this.route.params.subscribe(async (params) => {
      this.tipoEntidad = params['tipo'] as TipoEntidadConfigPerfil;

      switch (this.tipoEntidad) {
        case 'Luminaria':
          this.puedeCrear = 'Configuraciones - Crear perfiles luminarias';
          this.puedeEditar = 'Configuraciones - Editar perfiles luminarias';
          this.puedeEliminar = 'Configuraciones - Eliminar perfiles luminarias';
          break;
      }

      this.botonCrear = {
        mostrar: this.helper.tienePermiso(this.puedeCrear),
        tooltip: 'Crear perfil',
        color: 'primary',
        tipo: 'material',
        icon: 'add',
        accion: () => this.crear(),
      };
      await this.listar();
    });
    this.setColumnas();
  }

  async ngOnDestroy() {
    this.configsPerfil$?.unsubscribe();
  }
}
